<script>
import JsonExcel from "vue-json-excel";
import { API } from "@/api";
import {
  MONTH_NAMES,
  buildLeaveRanges,
  decorateTimesheet,
  summariseTimesheet,
} from "@/utils/timesheetSummary";

// Number of timesheets fetched in parallel when the whole-month endpoint does
// not cover every generated timesheet.
const DOWNLOAD_BATCH_SIZE = 10;

// Whole-month lookups are large, so allow more headroom than the axios default
// while still failing instead of hanging forever.
const REQUEST_TIMEOUT_MS = 120000;

export default {
  components: {
    JsonExcel,
  },
  computed: {
    monthOptions() {
      return MONTH_NAMES.map((name, index) => ({
        value: index + 1,
        text: name,
      }));
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear + 1; year >= currentYear - 6; year--) {
        years.push({ value: year, text: `${year}` });
      }
      return years;
    },
    downloadFileName() {
      const month = MONTH_NAMES[this.month - 1] || this.month;
      return `Country_Program_Timesheets_${month}_${this.year}.xls`;
    },
    downloadFields() {
      return {
        "S/N": "sn",
        T7: "t7",
        D7: "d7",
        Name: "employee",
        Position: "position",
        Location: "location",
        "Sector (T3)": "sector",
        "Contract Start Date": "contract_date",
        "Contract End Date": "contract_end_date",
        Period: "period",
        "Payroll Month": "payroll_month",
        "Payroll Year": "payroll_year",
        Status: "status",
        "Days Worked": "days_worked",
        "Public Holidays": "public_holiday_days",
        Weekends: "weekend_days",
        "Maternity Leave Days": "maternity_leave_days",
        "Paternity Leave Days": "paternity_leave_days",
        "Parental Leave Days": "parental_leave_days",
        "Other Leave Days": "other_leave_days",
        "Leave Types Taken": "leave_types",
        "Days Absent": "absent_days",
        "Total Time Worked": "total_time_worked",
        "Total Hours (Decimal)": "total_hours_decimal",
        "Reference No": "ref_no",
      };
    },
  },
  data() {
    const now = new Date();
    return {
      selectPeriod: false,
      month: now.getMonth() + 1,
      year: now.getFullYear(),
      downloading: false,
      processed: 0,
      total: 0,
      message: "",
    };
  },
  methods: {
    /**
     * Collects every timesheet already generated for the whole Country Program
     * in the selected month and returns one summary row per employee. Passed to
     * <JsonExcel :fetch> so the workbook is built from the resolved rows.
     */
    async fetchTimesheets() {
      if (this.downloading) return [];

      const month = parseInt(this.month, 10);
      const year = parseInt(this.year, 10);

      if (!month || !year) {
        this.apiFormHandler("Timesheets");
        return [];
      }

      this.downloading = true;
      this.processed = 0;
      this.total = 0;
      this.message = `Loading ${MONTH_NAMES[month - 1]} ${year} timesheets...`;

      try {
        // Whole-month endpoints keep this to a handful of requests instead of
        // one per employee. The month timesheet lookup is best-effort: when it
        // is unavailable each timesheet is pulled by reference instead.
        const [allocations, monthTimesheets, holidays, references, tracking] =
          await Promise.all([
            this.get(`${this.ROUTES.timeAllocation}/month-year/${month}/${year}`),
            this.getSafe(`${this.ROUTES.timesheet}/${month}/${year}`),
            this.get(`${this.ROUTES.publicHolidays}/holiday`),
            this.fetchEmployeeReferenceData(),
            this.fetchExpectedEmployees(month, year),
          ]);

        const submissions = this.collectSubmissions(allocations);
        const expectedEmployees = Array.isArray(tracking?.salaryEmployees)
          ? tracking.salaryEmployees
          : [];

        if (!submissions.length && !expectedEmployees.length) {
          this.resetProgress();
          this.apiResponseHandler(
            `No timesheet has been generated for ${
              MONTH_NAMES[month - 1]
            } ${year}.`,
            "Timesheets"
          );
          return [];
        }

        this.total = submissions.length + expectedEmployees.length;
        this.processed = 0;

        const timesheetsByRef = this.groupTimesheetRowsByRef(
          monthTimesheets?.timesheet
        );
        const resolved = [];
        const missing = [];

        submissions.forEach((submission) => {
          const timesheet = timesheetsByRef.get(submission.refNo);
          if (timesheet && timesheet.length) {
            resolved.push({ submission, timesheet });
            this.processed += 1;
          } else {
            missing.push(submission);
          }
        });

        // The month endpoint skips timesheets that already carry a status, so
        // pull only those by reference.
        if (missing.length) {
          this.message = `Fetching ${missing.length} remaining timesheet(s)...`;
          await this.runInBatches(missing, async (submission) => {
            const timesheet = await this.fetchTimesheetByRef(
              submission,
              month,
              year
            );
            if (timesheet) {
              resolved.push({ submission, timesheet });
            } else {
              resolved.push({ submission, timesheet: [] });
            }
            this.processed += 1;
          });
        }

        const submittedEmpIds = new Set(
          resolved.map(({ submission }) => Number(submission.empId))
        );
        const notSubmitted = expectedEmployees.filter((salaryRow) => {
          const empId = Number(salaryRow?.employee?.emp_id || salaryRow?.salary_empid);
          return empId && !submittedEmpIds.has(empId);
        });

        if (!resolved.length && !notSubmitted.length) {
          this.resetProgress();
          this.apiResponseHandler(
            "The generated timesheets could not be read. Please try again.",
            "Timesheets"
          );
          return [];
        }

        const leavesByEmployee = await this.fetchLeaveRangesForLeaveTakers(
          resolved.filter(({ timesheet }) => timesheet && timesheet.length)
        );

        this.message = `Preparing download for ${
          resolved.length + notSubmitted.length
        } employee(s)...`;
        const rows = resolved.map(({ submission, timesheet }) =>
          this.buildRow(
            submission,
            timesheet,
            month,
            year,
            holidays,
            leavesByEmployee,
            references
          )
        );
        notSubmitted.forEach((salaryRow) => {
          rows.push(this.buildNotSubmittedRow(salaryRow, month, year, references));
        });

        rows.sort((a, b) => a.employee.localeCompare(b.employee));
        rows.forEach((row, index) => {
          row.sn = index + 1;
        });

        return rows;
      } catch (error) {
        this.resetProgress();
        // apiErrorHandler reads every response-less error as a lost
        // connection, so only report request failures through it.
        if (error?.isAxiosError) {
          this.apiErrorHandler(error, "Timesheets");
        } else {
          this.$bvToast.toast(
            `The timesheets could not be prepared: ${error?.message || error}`,
            {
              title: "Timesheets",
              toaster: "b-toaster-top-right",
              appendToast: true,
              variant: "danger",
            }
          );
        }
        return [];
      } finally {
        this.downloading = false;
      }
    },
    // Bypasses apiGet so the bulk download does not toggle the page-wide
    // apiBusy loader while it runs.
    async get(url) {
      const res = await API.get(url, {
        ...this.apiConfig(),
        timeout: REQUEST_TIMEOUT_MS,
      });
      return res.data;
    },
    async getSafe(url) {
      try {
        return await this.get(url);
      } catch (error) {
        return null;
      }
    },
    async runInBatches(items, handler) {
      for (let start = 0; start < items.length; start += DOWNLOAD_BATCH_SIZE) {
        const batch = items.slice(start, start + DOWNLOAD_BATCH_SIZE);
        await Promise.all(batch.map((item) => handler(item)));
      }
    },
    async fetchEmployeeReferenceData() {
      const [locations, departments, jobRoles] = await Promise.all([
        this.getSafe(this.ROUTES.location),
        this.getSafe(this.ROUTES.department),
        this.getSafe(this.ROUTES.jobRole),
      ]);
      return this.mapEmployeeProfiles(locations, departments, jobRoles);
    },
    /**
     * Leave details are only needed to name the leave taken on a day, and the
     * full leave list is far too large to download. Most employees take no
     * leave in a month, so fetch per employee for just those who did.
     */
    async fetchLeaveRangesForLeaveTakers(resolved) {
      const leaveTakers = new Set();
      resolved.forEach(({ submission, timesheet }) => {
        if (timesheet.some((row) => row.ts_is_present === 4)) {
          leaveTakers.add(submission.empId);
        }
      });

      const leavesByEmployee = new Map();
      if (!leaveTakers.size) return leavesByEmployee;

      this.message = `Reading leave records for ${leaveTakers.size} employee(s)...`;
      await this.runInBatches(Array.from(leaveTakers), async (empId) => {
        const response = await this.getSafe(
          `${this.ROUTES.leaveApplication}/get-employee-leave/${empId}`
        );
        leavesByEmployee.set(empId, buildLeaveRanges(response?.data));
      });

      return leavesByEmployee;
    },
    async fetchExpectedEmployees(month, year) {
      try {
        const res = await API.post(
          `${this.ROUTES.timeAllocation}/timesheet-application-tracking-report`,
          { month, year, location: 0 },
          {
            ...this.apiConfig(),
            timeout: REQUEST_TIMEOUT_MS,
          }
        );
        return res.data;
      } catch (error) {
        return { salaryEmployees: [] };
      }
    },
    collectSubmissions(allocations) {
      const submissions = new Map();
      if (!Array.isArray(allocations)) return [];
      allocations.forEach((allocation) => {
        if (!allocation?.ta_ref_no || submissions.has(allocation.ta_ref_no)) {
          return;
        }
        submissions.set(allocation.ta_ref_no, {
          refNo: allocation.ta_ref_no,
          empId: allocation.ta_emp_id,
          status: allocation.ta_status,
          employee: allocation.employee,
        });
      });
      return Array.from(submissions.values());
    },
    groupTimesheetRowsByRef(timesheetRows) {
      const grouped = new Map();
      if (!Array.isArray(timesheetRows)) return grouped;
      timesheetRows.forEach((row) => {
        if (!row?.ts_ref_no) return;
        if (!grouped.has(row.ts_ref_no)) grouped.set(row.ts_ref_no, []);
        grouped.get(row.ts_ref_no).push(row);
      });
      return grouped;
    },
    // Location, sector and position live on small reference tables, so resolve
    // them from the employee's own ids rather than a per-employee lookup.
    mapEmployeeProfiles(locations, departments, jobRoles) {
      const index = (list, idKey, format) => {
        const map = new Map();
        if (!Array.isArray(list)) return map;
        list.forEach((entry) => {
          if (entry?.[idKey] === undefined) return;
          map.set(entry[idKey], format(entry));
        });
        return map;
      };

      return {
        locations: index(
          locations,
          "location_id",
          (l) => `${l.location_name} (${l.l_t6_code})`
        ),
        // The departments endpoint wraps its list alongside the sector leads.
        sectors: index(
          departments?.departments,
          "department_id",
          (d) => `${d.department_name} (${d.d_t3_code})`
        ),
        positions: index(jobRoles, "job_role_id", (j) => j.job_role),
      };
    },
    employeeProfile(employee, profiles) {
      return {
        location: profiles.locations.get(employee?.emp_location_id) || "",
        sector: profiles.sectors.get(employee?.emp_department_id) || "",
        position: profiles.positions.get(employee?.emp_job_role_id) || "",
      };
    },
    async fetchTimesheetByRef(submission, month, year) {
      const { refNo, empId } = submission;
      const data = await this.getSafe(
        `${this.ROUTES.timesheet}/time-sheet/${month}/${year}/${empId}/${refNo}`
      );
      const timesheet = data?.timesheet;
      return timesheet && timesheet.length ? timesheet : null;
    },
    buildRow(
      submission,
      timesheet,
      month,
      year,
      holidays,
      leavesByEmployee,
      profiles
    ) {
      const { refNo, empId, employee, status } = submission;
      const decorated = decorateTimesheet(
        timesheet || [],
        leavesByEmployee.get(empId) || [],
        holidays
      );
      const summary = summariseTimesheet(decorated);
      const profile = this.employeeProfile(employee, profiles);
      const leaveTypes = Array.from(
        new Set(
          decorated
            .filter((row) => row.ts_is_present === 4)
            .map((row) => row.leaveType || "ON LEAVE")
        )
      ).sort();

      return {
        sn: 0,
        ref_no: refNo,
        t7: employee?.emp_unique_id || "",
        d7: employee?.emp_d7 || "",
        employee: `${employee?.emp_first_name || ""} ${
          employee?.emp_last_name || ""
        }`.trim(),
        position: profile.position || "",
        location: profile.location || "",
        sector: profile.sector || "",
        contract_date: this.formatDate(employee?.emp_hire_date),
        contract_end_date: this.formatDate(employee?.emp_contract_end_date),
        period: `${MONTH_NAMES[month - 1] || month} ${year}`,
        payroll_month: MONTH_NAMES[month - 1] || month,
        payroll_year: year,
        status: this.timesheetStatusLabel(status),
        days_worked: summary.daysWorked,
        public_holiday_days: summary.publicHolidayDays,
        weekend_days: summary.weekendDays,
        maternity_leave_days: summary.maternityLeaveDays,
        paternity_leave_days: summary.paternityLeaveDays,
        parental_leave_days: summary.parentalLeaveDays,
        other_leave_days: summary.otherLeaveDays,
        leave_types: leaveTypes.join(", "),
        absent_days: summary.absentDays,
        total_time_worked: `${summary.totalHours} hrs ${summary.totalMinutes} mins`,
        total_hours_decimal: summary.decimalHours,
      };
    },
    timesheetStatusLabel(status) {
      switch (parseInt(status, 10)) {
        case 1:
          return "Approved";
        case 2:
          return "Discarded";
        case 0:
          return "Pending approval";
        default:
          return "Pending approval";
      }
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
    },
    buildNotSubmittedRow(salaryRow, month, year, profiles) {
      const employee = salaryRow?.employee || {};
      const profile = this.employeeProfile(employee, profiles);
      const locationName = salaryRow?.location
        ? `${salaryRow.location.location_name || ""} (${
            salaryRow.location.l_t6_code || ""
          })`.trim()
        : profile.location;
      const sector = employee.sector
        ? `${employee.sector.department_name} (${employee.sector.d_t3_code})`
        : profile.sector;
      return {
        sn: 0,
        ref_no: "",
        t7: employee.emp_unique_id || salaryRow?.salary_emp_unique_id || "",
        d7: employee.emp_d7 || salaryRow?.salary_d7 || "",
        employee: `${employee.emp_first_name || ""} ${
          employee.emp_last_name || ""
        }`.trim(),
        position: salaryRow?.jobrole?.job_role || profile.position || "",
        location: locationName || "",
        sector: sector || "",
        contract_date: this.formatDate(
          employee.emp_hire_date || salaryRow?.salary_emp_start_date
        ),
        contract_end_date: this.formatDate(
          employee.emp_contract_end_date || salaryRow?.salary_emp_end_date
        ),
        period: `${MONTH_NAMES[month - 1] || month} ${year}`,
        payroll_month: MONTH_NAMES[month - 1] || month,
        payroll_year: year,
        status: "Not submitted",
        days_worked: 0,
        public_holiday_days: 0,
        weekend_days: 0,
        maternity_leave_days: 0,
        paternity_leave_days: 0,
        parental_leave_days: 0,
        other_leave_days: 0,
        leave_types: "",
        absent_days: 0,
        total_time_worked: "0 hrs 0 mins",
        total_hours_decimal: 0,
      };
    },
    resetProgress() {
      this.message = "";
      this.processed = 0;
      this.total = 0;
    },
  },
};
</script>

<template>
  <div class="card">
    <div class="card-body">
      <div class="media">
        <div class="media-body overflow-hidden">
          <h4 class="mb-0">Timesheet Download</h4>
        </div>
        <div class="text-secondary"></div>
      </div>
    </div>
    <div class="card-body border-top py-2">
      <div
        class="d-flex align-items-center text-success d-inline-flex"
        style="cursor: pointer"
        @click="selectPeriod = true"
      >
        <span class="mr-2">Download</span>
        <i class="ri-arrow-right-s-line"></i>
      </div>
    </div>
    <b-modal
      v-model="selectPeriod"
      title="Download Timesheets"
      hide-footer
      centered
      title-class="font-18"
      size="md"
      no-close-on-backdrop
      no-close-on-esc
    >
      <p class="text-muted font-size-12">
        Download Country Program timesheets for a selected month, including
        employees who have not submitted.
      </p>
      <div class="form-row">
        <div class="form-group col-7">
          <label for="downloadMonth">Month</label>
          <b-form-select
            id="downloadMonth"
            v-model="month"
            :options="monthOptions"
            :disabled="downloading"
          ></b-form-select>
        </div>
        <div class="form-group col-5">
          <label for="downloadYear">Year</label>
          <b-form-select
            id="downloadYear"
            v-model="year"
            :options="yearOptions"
            :disabled="downloading"
          ></b-form-select>
        </div>
      </div>
      <button
        class="btn btn-success btn-block"
        :class="{ disabled: downloading }"
      >
        <JsonExcel
          style="cursor: pointer"
          :fetch="fetchTimesheets"
          :fields="downloadFields"
          :before-finish="resetProgress"
          :name="downloadFileName"
          worksheet="Timesheets"
        >
          <span v-if="downloading">
            <i class="mdi mdi-spin mdi-loading mr-2"></i>
            Preparing download...
          </span>
          <span v-else>
            <i class="mdi mdi-download mr-2"></i>
            Download Timesheets
          </span>
        </JsonExcel>
      </button>
      <div v-if="message" class="mt-3">
        <small class="text-muted d-block">{{ message }}</small>
        <b-progress
          v-if="total"
          :value="processed"
          :max="total"
          height="6px"
          class="mt-2"
          variant="success"
        ></b-progress>
      </div>
    </b-modal>
  </div>
</template>
